import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  createCritSession,
  deleteCritHeading,
  deleteCritSession,
  describeApiError,
  getCritSchedule,
  importCritSchedule,
  saveCritHeading,
  updateCritSession,
  type CritHeading,
  type CritImportSummary,
  type CritSchedule as Schedule,
  type CritSession,
  type CritSessionInput,
  type SessionKind,
} from '../lib/touchscreens';

const DEFAULT_WEEK_OF = '2026-11-18';

function dayLabel(date: string) {
  return new Date(`${date}T12:00:00`).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'short' });
}

/**
 * Admin tools → Crit schedule. Import emap's crit room spreadsheet (preview, then
 * import), then fix anything by hand. Screens only change when someone publishes
 * on the Touchscreens page, so editing here is safe at any time.
 */
export function CritSchedule() {
  const [schedule, setSchedule] = useState<Schedule | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [day, setDay] = useState<string | null>(null);
  const [room, setRoom] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ session: CritSession | null; draft: CritSessionInput } | null>(null);
  const [headingEdit, setHeadingEdit] = useState<{ heading: CritHeading | null; title: string; sponsor: string } | null>(null);

  const refresh = useCallback(async () => {
    try {
      const s = await getCritSchedule();
      setSchedule(s);
      setDay((d) => (d && s.dates.includes(d) ? d : (s.dates[0] ?? null)));
    } catch (e) {
      setError(describeApiError(e, 'Could not load the schedule.'));
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const roomsForDay = useMemo(() => {
    if (!schedule || !day) return [];
    const counts = new Map<string, { sessions: number; problems: number }>();
    for (const s of schedule.sessions.filter((x) => x.date === day)) {
      const c = counts.get(s.room) ?? { sessions: 0, problems: 0 };
      c.sessions++;
      if (s.problem) c.problems++;
      counts.set(s.room, c);
    }
    for (const h of schedule.headings.filter((x) => x.date === day)) if (!counts.has(h.room)) counts.set(h.room, { sessions: 0, problems: 0 });
    return schedule.rooms.filter((r) => counts.has(r.id)).map((r) => ({ ...r, ...counts.get(r.id)! }));
  }, [schedule, day]);

  useEffect(() => {
    if (roomsForDay.length && !roomsForDay.some((r) => r.id === room)) setRoom(roomsForDay[0].id);
  }, [roomsForDay, room]);

  const sessions = schedule?.sessions.filter((s) => s.date === day && s.room === room) ?? [];
  const headings = schedule?.headings.filter((h) => h.date === day && h.room === room) ?? [];
  const roomName = schedule?.rooms.find((r) => r.id === room)?.name ?? room;
  const problems = schedule?.sessions.filter((s) => s.problem).length ?? 0;

  const openNew = () =>
    setEditing({
      session: null,
      draft: { date: day ?? DEFAULT_WEEK_OF, room: room ?? '1', start: '10:00', end: '10:20', kind: 'event', heading: headings[0]?.title ?? null },
    });

  const openEdit = (s: CritSession) =>
    setEditing({
      session: s,
      draft: {
        date: s.date,
        room: s.room,
        start: s.start,
        end: s.end,
        kind: s.kind,
        nomination_code: s.nomination_code,
        title: s.title,
        subtitle: s.subtitle,
        heading: s.heading,
      },
    });

  const saveSession = async () => {
    if (!editing) return;
    try {
      if (editing.session) await updateCritSession(editing.session.id, editing.draft);
      else await createCritSession(editing.draft);
      setEditing(null);
      await refresh();
    } catch (e) {
      setError(describeApiError(e, 'Could not save that slot.'));
    }
  };

  const removeSession = async (s: CritSession) => {
    if (!window.confirm(`Delete the ${s.start} slot in ${roomName}?`)) return;
    try {
      await deleteCritSession(s.id);
      await refresh();
    } catch (e) {
      setError(describeApiError(e, 'Could not delete that slot.'));
    }
  };

  const saveHeading = async () => {
    if (!headingEdit || !day || !room) return;
    try {
      await saveCritHeading(headingEdit.heading?.id ?? null, {
        date: day,
        room,
        title: headingEdit.title,
        sponsor: headingEdit.sponsor || null,
        sort_order: headingEdit.heading?.sort_order ?? headings.length,
      });
      setHeadingEdit(null);
      await refresh();
    } catch (e) {
      setError(describeApiError(e, 'Could not save the heading.'));
    }
  };

  return (
    <Stack spacing={3}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'flex-end' }} spacing={1}>
        <Box>
          <Typography variant="h3" component="h1">
            Crit schedule
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            What the touchscreens outside each crit room show. Changes go live when you{' '}
            <Box component={RouterLink} to="/touchscreens" sx={{ color: 'inherit' }}>
              publish on the Touchscreens page
            </Box>
            .
          </Typography>
        </Box>
        {problems > 0 && <Chip color="warning" icon={<WarningAmberIcon />} label={`${problems} slot${problems === 1 ? '' : 's'} need checking`} />}
      </Stack>

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <ImportPanel onImported={refresh} hasSchedule={(schedule?.sessions.length ?? 0) > 0} />

      {schedule && schedule.dates.length > 0 && day && (
        <Paper sx={{ overflow: 'hidden' }}>
          <Tabs value={day} onChange={(_, d) => setDay(d)} variant="scrollable" sx={{ borderBottom: 1, borderColor: 'divider', px: 1 }}>
            {schedule.dates.map((d) => (
              <Tab key={d} value={d} label={dayLabel(d)} />
            ))}
          </Tabs>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '240px minmax(0, 1fr)' } }}>
            <Box sx={{ borderRight: { md: 1 }, borderBottom: { xs: 1, md: 0 }, borderColor: 'divider', maxHeight: { md: 640 }, overflowY: 'auto' }}>
              <List dense disablePadding>
                {roomsForDay.map((r) => (
                  <ListItemButton key={r.id} selected={r.id === room} onClick={() => setRoom(r.id)}>
                    <ListItemText primary={r.name} secondary={`${r.sessions} slot${r.sessions === 1 ? '' : 's'}`} />
                    {r.problems > 0 && <WarningAmberIcon color="warning" fontSize="small" />}
                  </ListItemButton>
                ))}
              </List>
            </Box>
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h5">{roomName}</Typography>
                <Button startIcon={<AddIcon />} variant="outlined" color="inherit" onClick={openNew}>
                  Add slot
                </Button>
              </Stack>

              <Stack spacing={1} sx={{ mb: 2 }}>
                {headings.map((h) => (
                  <Stack key={h.id} direction="row" spacing={1} alignItems="center">
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontWeight: 600 }}>{h.title}</Typography>
                      {h.sponsor && (
                        <Typography variant="body2" color="text.secondary">
                          {h.sponsor}
                        </Typography>
                      )}
                    </Box>
                    <IconButton size="small" aria-label="Edit heading" onClick={() => setHeadingEdit({ heading: h, title: h.title, sponsor: h.sponsor ?? '' })}>
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      aria-label="Delete heading"
                      onClick={async () => {
                        if (!window.confirm(`Remove the heading "${h.title}"?`)) return;
                        await deleteCritHeading(h.id);
                        await refresh();
                      }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                ))}
                <Box>
                  <Button size="small" color="inherit" startIcon={<AddIcon />} onClick={() => setHeadingEdit({ heading: null, title: '', sponsor: '' })}>
                    Add category heading
                  </Button>
                </Box>
              </Stack>

              <Stack divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />}>
                {sessions.length === 0 && (
                  <Typography color="text.secondary" sx={{ py: 2 }}>
                    Nothing in this room on this day.
                  </Typography>
                )}
                {sessions.map((s) => (
                  <SessionRow key={s.id} session={s} onEdit={() => openEdit(s)} onDelete={() => void removeSession(s)} />
                ))}
              </Stack>
            </Box>
          </Box>
        </Paper>
      )}

      {editing && (
        <SessionDialog
          draft={editing.draft}
          isNew={!editing.session}
          headings={schedule?.headings.filter((h) => h.date === editing.draft.date && h.room === editing.draft.room) ?? []}
          rooms={schedule?.rooms ?? []}
          onChange={(draft) => setEditing({ ...editing, draft })}
          onClose={() => setEditing(null)}
          onSave={() => void saveSession()}
        />
      )}

      {headingEdit && (
        <Dialog open onClose={() => setHeadingEdit(null)} fullWidth maxWidth="sm">
          <DialogTitle>{headingEdit.heading ? 'Edit heading' : 'Add category heading'}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField label="Category" value={headingEdit.title} onChange={(e) => setHeadingEdit({ ...headingEdit, title: e.target.value })} autoFocus />
              <TextField
                label="Sponsor line (optional)"
                placeholder="Supported by GROHE"
                value={headingEdit.sponsor}
                onChange={(e) => setHeadingEdit({ ...headingEdit, sponsor: e.target.value })}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button color="inherit" onClick={() => setHeadingEdit(null)}>
              Cancel
            </Button>
            <Button variant="contained" onClick={() => void saveHeading()} disabled={!headingEdit.title.trim()}>
              Save
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Stack>
  );
}

function SessionRow({ session: s, onEdit, onDelete }: { session: CritSession; onEdit: () => void; onDelete: () => void }) {
  const title = s.kind === 'entry' ? (s.title ?? s.project_title ?? s.nomination_code) : s.title;
  const subtitle = s.kind === 'entry' ? (s.subtitle ?? s.architect) : s.subtitle;
  return (
    <Stack direction="row" spacing={2} alignItems="center" sx={{ py: 1.25, opacity: s.kind === 'break' ? 0.7 : 1 }}>
      <Typography sx={{ width: 104, flexShrink: 0, fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
        {s.start}–{s.end}
      </Typography>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography sx={{ fontWeight: s.kind === 'break' ? 400 : 600 }} noWrap>
            {title}
          </Typography>
          {s.kind === 'event' && <Chip size="small" label="Event" color="primary" />}
          {s.source === 'manual' && (
            <Tooltip title="Added or edited by hand — kept when the spreadsheet is re-imported">
              <Chip size="small" label="Edited" variant="outlined" />
            </Tooltip>
          )}
        </Stack>
        <Typography variant="body2" color="text.secondary" noWrap>
          {[subtitle, s.kind === 'entry' ? s.nomination_code : null].filter(Boolean).join(' · ')}
        </Typography>
        {s.problem && (
          <Typography variant="body2" color="warning.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <WarningAmberIcon sx={{ fontSize: 16 }} /> {s.problem}
          </Typography>
        )}
      </Box>
      {s.nomination_uuid && (
        <Button size="small" color="inherit" component={RouterLink} to={`/entry/${s.nomination_uuid}`}>
          Entry
        </Button>
      )}
      <IconButton size="small" aria-label="Edit slot" onClick={onEdit}>
        <EditOutlinedIcon fontSize="small" />
      </IconButton>
      <IconButton size="small" aria-label="Delete slot" onClick={onDelete}>
        <DeleteOutlineIcon fontSize="small" />
      </IconButton>
    </Stack>
  );
}

function SessionDialog({
  draft,
  isNew,
  headings,
  rooms,
  onChange,
  onClose,
  onSave,
}: {
  draft: CritSessionInput;
  isNew: boolean;
  headings: CritHeading[];
  rooms: Array<{ id: string; name: string }>;
  onChange: (d: CritSessionInput) => void;
  onClose: () => void;
  onSave: () => void;
}) {
  const set = (patch: Partial<CritSessionInput>) => onChange({ ...draft, ...patch });
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isNew ? 'Add slot' : 'Edit slot'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField select label="Type" value={draft.kind} onChange={(e) => set({ kind: e.target.value as SessionKind })}>
            <MenuItem value="entry">Entry presenting</MenuItem>
            <MenuItem value="event">Event (workshop, talk, round table)</MenuItem>
            <MenuItem value="break">Break</MenuItem>
          </TextField>
          <Stack direction="row" spacing={2}>
            <TextField label="Date" type="date" value={draft.date} onChange={(e) => set({ date: e.target.value })} slotProps={{ inputLabel: { shrink: true } }} fullWidth />
            <TextField select label="Room" value={draft.room} onChange={(e) => set({ room: e.target.value })} fullWidth>
              {rooms.map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  {r.name}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField label="Start" type="time" value={draft.start} onChange={(e) => set({ start: e.target.value })} slotProps={{ inputLabel: { shrink: true } }} fullWidth />
            <TextField label="End" type="time" value={draft.end} onChange={(e) => set({ end: e.target.value })} slotProps={{ inputLabel: { shrink: true } }} fullWidth />
          </Stack>
          {draft.kind === 'entry' && (
            <TextField
              label="Project number"
              value={draft.nomination_code ?? ''}
              onChange={(e) => set({ nomination_code: e.target.value.toUpperCase().trim() })}
              helperText="The 15-character WBD project number"
            />
          )}
          <TextField
            label={draft.kind === 'entry' ? 'Title (optional — defaults to the project title)' : 'Title'}
            value={draft.title ?? ''}
            onChange={(e) => set({ title: e.target.value })}
            placeholder={draft.kind === 'event' ? 'Scribbles to Stunners: Your Chaos-Powered Visualization Toolkit' : draft.kind === 'break' ? 'Lunch' : ''}
          />
          {draft.kind !== 'break' && (
            <TextField
              label={draft.kind === 'entry' ? 'Practice (optional — defaults to the entry architect)' : 'Speaker / host (optional)'}
              value={draft.subtitle ?? ''}
              onChange={(e) => set({ subtitle: e.target.value })}
            />
          )}
          {draft.kind !== 'break' && headings.length > 0 && (
            <TextField select label="Category heading" value={draft.heading ?? ''} onChange={(e) => set({ heading: e.target.value || null })}>
              <MenuItem value="">None</MenuItem>
              {headings.map((h) => (
                <MenuItem key={h.id} value={h.title}>
                  {h.title}
                </MenuItem>
              ))}
            </TextField>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={onSave}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ImportPanel({ onImported, hasSchedule }: { onImported: () => Promise<void>; hasSchedule: boolean }) {
  const [file, setFile] = useState<File | null>(null);
  const [weekOf, setWeekOf] = useState(DEFAULT_WEEK_OF);
  const [preview, setPreview] = useState<CritImportSummary | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = async (commit: boolean) => {
    if (!file) return;
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      const result = await importCritSchedule(file, weekOf, commit);
      if (commit) {
        setPreview(null);
        setFile(null);
        setDone(`Imported ${result.sessions} slots across ${result.days.length} days.`);
        await onImported();
      } else {
        setPreview(result);
      }
    } catch (e) {
      setError(describeApiError(e, 'Import failed.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper sx={{ p: { xs: 2, sm: 3 } }}>
      <Stack spacing={2}>
        <Typography variant="h6">Import the crit schedule spreadsheet</Typography>
        <Typography variant="body2" color="text.secondary">
          Upload emap's "Crit Room Allocation and Presentation Timings" .xlsx. You'll see a preview first. Importing again later replaces the imported
          slots for those days — anything you've added or edited here by hand is kept.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <Button component="label" variant="outlined" color="inherit" startIcon={<UploadFileOutlinedIcon />} disabled={busy}>
            {file ? file.name : 'Choose .xlsx'}
            <input
              hidden
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(e) => {
                setFile(e.target.files?.[0] ?? null);
                setPreview(null);
                setDone(null);
              }}
            />
          </Button>
          <TextField
            size="small"
            label="Festival week (any date)"
            type="date"
            value={weekOf}
            onChange={(e) => setWeekOf(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <Button variant="contained" disabled={!file || busy} onClick={() => void run(false)}>
            Preview
          </Button>
        </Stack>
        {error && <Alert severity="error">{error}</Alert>}
        {done && <Alert severity="success">{done}</Alert>}
        {preview && (
          <Stack spacing={2}>
            <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' } }}>
              {preview.days.map((d) => (
                <Paper key={d.date} variant="outlined" sx={{ p: 1.5 }}>
                  <Typography sx={{ fontWeight: 600 }}>{dayLabel(d.date)}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {d.rooms} rooms · {d.entries} entries · {d.events} events · {d.breaks} breaks
                  </Typography>
                </Paper>
              ))}
            </Box>
            {preview.unmatched_codes.length > 0 && (
              <Alert severity="warning">
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {preview.unmatched_codes.length} project number{preview.unmatched_codes.length === 1 ? '' : 's'} don't match an entry (they'll be
                  imported and flagged so you can fix them):
                </Typography>
                {preview.unmatched_codes.map((c) => (
                  <Typography key={c} variant="body2">
                    {c}
                  </Typography>
                ))}
              </Alert>
            )}
            {preview.warnings.length > 0 && (
              <Alert severity="info">
                {preview.warnings.map((w) => (
                  <Typography key={w} variant="body2">
                    {w}
                  </Typography>
                ))}
              </Alert>
            )}
            <Stack direction="row" spacing={2} alignItems="center">
              <Button variant="contained" disabled={busy} onClick={() => void run(true)}>
                {hasSchedule ? 'Import and replace' : 'Import'}
              </Button>
              {hasSchedule && (
                <Typography variant="body2" color="text.secondary">
                  Replaces {preview.replaced_sessions} imported slots; keeps {preview.kept_manual_sessions} hand-edited.
                </Typography>
              )}
            </Stack>
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}
